import { useCallback, useState } from 'react'
import { IReviewFacilityAddressInput, IVendorIndustries, Maybe } from 'types/graphqlTypes'
import { IFacilitySummaryProps } from '../FacilitySummary'
import { TVendorSchema } from '../VendorForm/VendorFormSchema'
import { useReviewFacilityAddressMutation } from './graphql/ReviewFacilityAddress'
import { useVendorFormMutation } from './graphql/VendorForm'

export interface IVendorFormProps {
  facilityUuid: string
  shipperFacilityUuid: string
}

export const useReviewFacility = () => {
  const [_reviewFacility] = useReviewFacilityAddressMutation()

  const reviewFacility = useCallback(
    async (input: IReviewFacilityAddressInput) => {
      const { data } = await _reviewFacility({
        variables: {
          input,
        },
      })

      const { reviewFacilityAddress } = data || {}
      const { errors = [] } = reviewFacilityAddress || {}

      if (errors?.length) {
        return ['error', errors[0].message]
      }

      return ['success', null]
    },
    [_reviewFacility]
  )

  return {
    reviewFacility,
  }
}

export const useVendorFormAction = () => {
  const [_vendorAction] = useVendorFormMutation()
  const [vendor, setVendor] = useState<TVendorSchema>()

  const vendorForm = useCallback(
    async (facility: Maybe<IFacilitySummaryProps['facility']>) => {
      if (vendor) {
        const addVendor = !vendor?.vendorId
        const { data } = await _vendorAction({
          variables: {
            addInput: {
              name: vendor.name,
              industry: (vendor.industry as IVendorIndustries) || undefined,
              vendorKeywords: vendor.vendorKeywords.map(value => ({ value })),
            },
            editInput: {
              vendorId: vendor.vendorId,
              name: vendor.name,
              industry: (vendor.industry as IVendorIndustries) || undefined,
              facilityUuid: facility?.uuid || '',
              shipperFacilities:
                facility?.shipperFacilities?.map(({ uuid, identifiers = [] }) => ({
                  uuid,
                  identifiers: identifiers.map(({ id }) => ({ id })),
                  metadata: { addressLine2: '' },
                })) || [],
              addressLine2: vendor.vendorFacilityAddressLine2,
            },
            addVendor,
          },
        })

        const response = addVendor ? data?.createVendor : data?.assignFacilityVendor
        const { errors = [] } = response || {}

        if (errors?.length) {
          return ['error', errors[0].message]
        }

        return ['success', null]
      }

      return ['skipped', null]
    },
    [_vendorAction, vendor]
  )

  return {
    vendor,
    vendorForm,
    setVendor,
  }
}
